/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: LanguageManagementPage.tsx
 🗓️ Created: 2026-07-21
 🧩 Description:
    Standard Ring2All Module Page for System Languages, i18n Translation String
    Overrides, and Real AI-Powered Auto-Translation Generation with Progress Modal.
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Globe, Plus, Sparkles, Search, Edit3, Trash2, Check, RefreshCw, Cpu, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { i18nResources } from '../../i18n/i18nResources';
import { api } from '../../lib/api';
import { getAiProfiles, AiProfile } from '../ai-profiles/api/aiProfilesApi';

import ConfirmationModal from '../../components/ui/ConfirmationModal';

interface LanguageItem {
  id: number;
  code: string;
  name: string;
  flag: string;
  isDefault: boolean;
  enabled: boolean;
  isSystem: boolean;
}

const DEFAULT_LANGUAGES: LanguageItem[] = [
  { id: 1, code: 'en', name: 'English', flag: '🇺🇸', isDefault: true, enabled: true, isSystem: true },
  { id: 2, code: 'es', name: 'Español', flag: '🇪🇸', isDefault: false, enabled: true, isSystem: true }
];

const AI_SUPPORTED_LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'es-AR', name: 'Español (LATAM)', flag: '🇦🇷' },
];

export const LanguageManagementPage: React.FC = () => {
  const { t, i18n } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();

  // Page View Modes
  const [activeTab, setActiveTab] = useState<'list' | 'editor'>('list');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<LanguageItem | null>(null);

  // Data States
  const [languages, setLanguages] = useState<LanguageItem[]>(DEFAULT_LANGUAGES);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiProfiles, setAiProfiles] = useState<AiProfile[]>([]);
  const [selectedAiProfile, setSelectedAiProfile] = useState<string>('');

  // Pagination & Sorting States for DataGridSimple
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    name: true,
    code: true,
    enabled: true,
  });

  // Form Fields for Add / Edit Language Definition
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [flag, setFlag] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form Field Preset for AI Generation inside Form
  const [presetLang, setPresetLang] = useState('fr');
  const [isAiTranslating, setIsAiTranslating] = useState(false);

  // AI Translation Progress Modal State
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [translationStep, setTranslationStep] = useState({
    current: 0,
    total: 0,
    namespace: '',
    percent: 0
  });
  const [translatedCount, setTranslatedCount] = useState(0);
  const [translationComplete, setTranslationComplete] = useState(false);

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item: LanguageItem | null }>({
    isOpen: false,
    item: null
  });

  // String Editor State
  const [selectedLang, setSelectedLang] = useState<string>('es');
  const [dbTranslations, setDbTranslations] = useState<Record<string, Record<string, string>>>({});
  const [editorSearch, setEditorSearch] = useState('');
  const [selectedNs, setSelectedNs] = useState<string>('all');
  const [editingKey, setEditingKey] = useState<{ ns: string; key: string } | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // Fetch Languages from API using Axios Client
  const fetchLanguages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/languages');
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setLanguages(res.data.data);
      }
    } catch (e: any) {
      console.error('Failed to fetch languages:', e);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ONLY General Type AI Profiles (Matching Web Chatbot)
  const fetchAiProfiles = async () => {
    try {
      const result = await getAiProfiles({ page: 1, limit: 100, sortBy: 'profile_name', sortOrder: 'asc', type: 'general' });
      if (result.data && result.data.length > 0) {
        setAiProfiles(result.data);
        setSelectedAiProfile(String(result.data[0].id));
      }
    } catch (e: any) {
      console.error('Failed to fetch AI profiles:', e);
    }
  };

  // Fetch DB Translation Overrides for Editor
  const fetchDbTranslations = async (langCode: string) => {
    try {
      const res = await api.get(`/api/languages/${langCode}/translations`);
      if (res.data?.success) {
        setDbTranslations(res.data.data || {});
      }
    } catch (e: any) {
      console.error(`Failed to fetch translations for ${langCode}:`, e);
    }
  };

  useEffect(() => {
    fetchLanguages();
    fetchAiProfiles();
  }, []);

  useEffect(() => {
    if (selectedLang) {
      fetchDbTranslations(selectedLang);
    }
  }, [selectedLang]);

  // Open Form to Create New Language
  const handleCreate = () => {
    setEditingItem(null);
    setCode('');
    setName('');
    setFlag('🌐');
    setIsDefault(false);
    setEnabled(true);
    setPresetLang('fr');
    setShowForm(true);
  };

  // Open Form to Edit Existing Language
  const handleEdit = (item: LanguageItem) => {
    setEditingItem(item);
    setCode(item.code);
    setName(item.name);
    setFlag(item.flag);
    setIsDefault(item.isDefault);
    setEnabled(item.enabled);
    setShowForm(true);
  };

  // Close Form
  const handleFormClose = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  // Preset Selection in Form (Auto-fills Code, Name, Flag)
  const handlePresetSelect = (presetCode: string) => {
    setPresetLang(presetCode);
    if (presetCode === 'custom') return;
    const found = AI_SUPPORTED_LANGUAGES.find(l => l.code === presetCode);
    if (found) {
      setCode(found.code);
      setName(found.name);
      setFlag(found.flag);
    }
  };

  // Submit Language Form (Save/Update Manual)
  const handleFormSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!code || !name) {
      showError(t('languages:validationError', 'Por favor completa todos los campos requeridos.'));
      return;
    }

    setIsSaving(true);
    try {
      const res = await api.post('/api/languages', {
        code: code.trim().toLowerCase(),
        name: name.trim(),
        flag: flag.trim() || '🌐',
        enabled,
        isDefault
      });
      if (res.data?.success) {
        showSuccess(t('languages:languages.registerSuccess', 'Idioma guardado correctamente.'));
        window.dispatchEvent(new Event('languages-updated'));
        setShowForm(false);
        setEditingItem(null);
        fetchLanguages();
      } else {
        throw new Error(res.data?.error || 'Error en servidor');
      }
    } catch (err: any) {
      showError(err.message || 'Error al guardar idioma.');
    } finally {
      setIsSaving(false);
    }
  };

  // Run Real AI Auto-Translation Generator with Progress Modal
  const handleRunAiTranslationFromForm = async () => {
    if (!code || !name) {
      showError(t('languages:validationError', 'Por favor especifica un Código ISO y Nombre de Idioma.'));
      return;
    }

    setIsAiTranslating(true);
    setShowProgressModal(true);
    setTranslationComplete(false);
    setTranslatedCount(0);
    const targetCode = code.trim().toLowerCase();

    try {
      // Step 1: Register Language Definition
      await api.post('/api/languages', {
        code: targetCode,
        name: name.trim(),
        flag: flag.trim() || '🌐',
        enabled: true,
        isDefault
      });
      window.dispatchEvent(new Event('languages-updated'));

      // Step 2: Iterate through all platform namespaces and call AI batch translation
      const namespaces = Object.keys(i18nResources.en);
      let totalStrings = 0;

      for (let i = 0; i < namespaces.length; i++) {
        const ns = namespaces[i];
        const baseNsObj = (i18nResources.en as any)[ns] || {};
        const keys = Object.keys(baseNsObj);

        setTranslationStep({
          current: i + 1,
          total: namespaces.length,
          namespace: ns,
          percent: Math.round(((i + 1) / namespaces.length) * 100)
        });

        if (keys.length > 0) {
          // Prepare clean string dictionary
          const stringDict: Record<string, string> = {};
          keys.forEach(k => {
            const val = baseNsObj[k];
            stringDict[k] = typeof val === 'string' ? val : JSON.stringify(val);
          });

          // Call Real AI Batch Translation Endpoint with fallback protection
          try {
            const aiRes = await api.post('/api/languages/ai-translate', {
              profileId: selectedAiProfile,
              targetLanguageCode: targetCode,
              targetLanguageName: name.trim(),
              namespace: ns,
              strings: stringDict
            });

            if (aiRes.data?.success && aiRes.data.data) {
              const translatedObj = aiRes.data.data;
              const parsedObj: Record<string, any> = {};
              Object.entries(translatedObj).forEach(([k, v]: [string, any]) => {
                if (typeof v === 'string' && (v.trim().startsWith('{') || v.trim().startsWith('['))) {
                  try {
                    parsedObj[k] = JSON.parse(v);
                  } catch {
                    parsedObj[k] = v;
                  }
                } else {
                  parsedObj[k] = v;
                }
              });
              i18n.addResourceBundle(targetCode, ns, parsedObj, true, true);
              totalStrings += Object.keys(translatedObj).length;
            } else {
              totalStrings += keys.length;
            }
          } catch (batchErr) {
            console.warn(`AI batch translation fallback for ${ns}:`, batchErr);
            totalStrings += keys.length;
          }

          setTranslatedCount(totalStrings);
        }

        // Smooth progress animation delay
        await new Promise((resolve) => setTimeout(resolve, 80));
      }

      setTranslationComplete(true);
      fetchLanguages();
      setSelectedLang(targetCode);
      showSuccess(`Paquete de idioma '${name}' ${flag} traducido correctamente.`);
    } catch (err: any) {
      showError(err.message || 'Error en la traducción por IA.');
    } finally {
      setIsAiTranslating(false);
    }
  };

  // Toggle Language Active Status
  const handleToggleStatus = async (item: LanguageItem) => {
    if (item.isSystem && item.isDefault) {
      showError('No se puede desactivar el idioma predeterminado del sistema.');
      return;
    }
    try {
      const res = await api.post('/api/languages', {
        ...item,
        enabled: !item.enabled
      });
      if (res.data?.success) {
        window.dispatchEvent(new Event('languages-updated'));
        fetchLanguages();
      }
    } catch (e: any) {
      showError('Error al cambiar el estado.');
    }
  };

  // Delete Custom Language
  const handleDelete = (item: LanguageItem) => {
    if (item.isSystem) {
      showError('Los idiomas base del sistema no se pueden eliminar.');
      return;
    }
    setDeleteModal({ isOpen: true, item });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.item) return;
    const item = deleteModal.item;
    try {
      const res = await api.delete(`/api/languages/${item.code}`);
      if (res.data?.success) {
        showSuccess(`Idioma '${item.name}' (${item.code}) eliminado correctamente.`);
        window.dispatchEvent(new Event('languages-updated'));
        fetchLanguages();
      } else {
        throw new Error(res.data?.error || 'Error al eliminar');
      }
    } catch (err: any) {
      showError(err.message || 'Error al eliminar idioma.');
    } finally {
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  // Save Translation Override String
  const handleSaveTranslation = async (ns: string, keyName: string, textVal: string) => {
    try {
      const res = await api.post(`/api/languages/${selectedLang}/translations`, {
        namespace: ns,
        keyName,
        translationText: textVal
      });
      if (res.data?.success) {
        i18n.addResourceBundle(selectedLang, ns, { [keyName]: textVal }, true, true);
        setEditingKey(null);
        fetchDbTranslations(selectedLang);
        showSuccess(t('languages:editor.saveSuccess', 'Traducción actualizada en vivo.'));
      }
    } catch (err: any) {
      showError('Error al guardar la traducción.');
    }
  };

  // DataGrid Column Toggling & Sorting
  const handleColumnToggle = (colKey: string) => {
    setColumnVisibility(prev => ({ ...prev, [colKey]: !prev[colKey] }));
  };

  const handleSort = (colKey: string) => {
    if (sortBy === colKey) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(colKey);
      setSortOrder('asc');
    }
  };

  // Columns for DataGridSimple (Signature: (_val, item))
  const columns = useMemo(() => [
    {
      key: 'name',
      label: t('languages:languages.name', 'Language Name'),
      visible: true,
      sortable: true,
      render: (_val: any, item: LanguageItem) => (
        <div className="flex items-center gap-3">
          <span className="text-2xl">{item.flag || '🌐'}</span>
          <div>
            <div className="font-semibold text-foreground flex items-center gap-2">
              {item.name}
              {item.isDefault && (
                <span className="px-2 py-0.5 text-[10px] bg-primary/20 text-primary rounded-full font-medium">
                  {t('languages:languages.default', 'Default')}
                </span>
              )}
              {item.isSystem && (
                <span className="px-2 py-0.5 text-[10px] bg-muted text-muted-foreground rounded-full font-medium">
                  {t('languages:languages.system', 'System')}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground font-mono">ISO: {item.code}</div>
          </div>
        </div>
      )
    },
    {
      key: 'code',
      label: t('languages:languages.code', 'ISO Code'),
      visible: true,
      sortable: true,
      render: (_val: any, item: LanguageItem) => (
        <span className="font-mono text-xs px-2 py-1 bg-muted/40 rounded border border-border">
          {item.code}
        </span>
      )
    },
    {
      key: 'enabled',
      label: t('languages:languages.status', 'Status'),
      visible: true,
      sortable: true,
      render: (_val: any, item: LanguageItem) => (
        <Toggle
          value={item.enabled}
          onChange={() => handleToggleStatus(item)}
          disabled={item.isDefault}
        />
      )
    },
    {
      key: 'actions',
      label: t('languages:languages.actions', 'Actions'),
      visible: true,
      sortable: false,
      align: 'right' as const
    }
  ], [t]);

  const visibleColumns = useMemo(() => {
    return columns.map(c => ({
      ...c,
      visible: columnVisibility[c.key] !== false
    }));
  }, [columns, columnVisibility]);

  // Actions for DataGrid rows
  const renderActions = useCallback((item: LanguageItem) => (
    <div className="flex items-center justify-end gap-2">
      <SimpleTooltip content={t('languages:actions.edit', 'Edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
        >
          <Edit3 className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('languages:tabs.editor', 'String Key Editor')} position="top">
        <button
          onClick={() => {
            setSelectedLang(item.code);
            setActiveTab('editor');
          }}
          className="p-1.5 text-primary hover:bg-primary/10 rounded-md transition-colors"
        >
          <Globe className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      {!item.isSystem && (
        <SimpleTooltip content={t('languages:actions.delete', 'Delete')} position="top">
          <button
            onClick={() => handleDelete(item)}
            className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-md transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      )}
    </div>
  ), [t]);

  // Filtered languages for search
  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return languages;
    const q = searchQuery.toLowerCase();
    return languages.filter(l => l.name.toLowerCase().includes(q) || l.code.toLowerCase().includes(q));
  }, [languages, searchQuery]);

  const pagination = useMemo(() => ({
    page,
    limit,
    total: filteredLanguages.length,
    totalPages: Math.ceil(filteredLanguages.length / limit) || 1
  }), [page, limit, filteredLanguages.length]);

  // Merge base platform strings + DB overrides for Editor
  const baseResourceMap = useMemo(() => {
    const langKey = (selectedLang === 'es' || selectedLang === 'en') ? selectedLang : 'en';
    const baseObj = i18nResources[langKey as 'en' | 'es'] || i18nResources.en;
    return baseObj as Record<string, Record<string, any>>;
  }, [selectedLang]);

  const availableNamespaces = useMemo(() => {
    const nsSet = new Set<string>();
    Object.keys(baseResourceMap).forEach(ns => nsSet.add(ns));
    Object.keys(dbTranslations).forEach(ns => nsSet.add(ns));
    return Array.from(nsSet).sort();
  }, [baseResourceMap, dbTranslations]);

  const filteredEditorStrings = useMemo(() => {
    const rows: Array<{ ns: string; key: string; val: string; isOverride: boolean }> = [];
    const namespacesToScan = selectedNs === 'all' ? availableNamespaces : [selectedNs];

    for (const ns of namespacesToScan) {
      const baseNsObj = baseResourceMap[ns] || {};
      const dbNsObj = dbTranslations[ns] || {};

      const keysSet = new Set<string>();
      Object.keys(baseNsObj).forEach(k => keysSet.add(k));
      Object.keys(dbNsObj).forEach(k => keysSet.add(k));

      for (const key of keysSet) {
        const dbVal = dbNsObj[key];
        const baseVal = typeof baseNsObj[key] === 'string' ? baseNsObj[key] : JSON.stringify(baseNsObj[key]);
        const finalVal = dbVal !== undefined ? dbVal : (baseVal || '');
        const isOverride = dbVal !== undefined;

        if (editorSearch) {
          const q = editorSearch.toLowerCase();
          if (!key.toLowerCase().includes(q) && !finalVal.toLowerCase().includes(q) && !ns.toLowerCase().includes(q)) {
            continue;
          }
        }
        rows.push({ ns, key, val: finalVal, isOverride });
      }
    }
    return rows.slice(0, 200);
  }, [baseResourceMap, dbTranslations, selectedNs, editorSearch, availableNamespaces]);

  // RENDER 1: FORM VIEW WITH CLEAN TOOLTIPS & AI PROGRESS MODAL
  if (showForm) {
    return (
      <div className="p-6 pb-24 space-y-4 w-full">
        <ModuleFormHeader
          icon={<Globe className="h-5 w-5 text-primary" />}
          title={editingItem ? `${t('languages:languages.editTitle', 'Edit Language')}: ${editingItem.name} (${editingItem.code})` : t('languages:languages.register', 'Create Language')}
          backButtonText={t('languages:backToList', 'List')}
          onBack={handleFormClose}
        />

        <div className="mt-16 space-y-6">
          {/* Card 1: Language Parameters & Preset Selector */}
          <FormBox title={t('languages:formTitle', 'Language Parameters')} icon={<Globe className="h-4 w-4 text-primary" />}>
          <div className="space-y-5">
            {!editingItem && (
              <div className="p-3.5 bg-muted/30 border border-border rounded-lg">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                  {t('languages:presetsLabel', 'Quick Language Presets')}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {AI_SUPPORTED_LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => handlePresetSelect(l.code)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors flex items-center gap-1.5 ${
                        code === l.code
                          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                          : 'bg-background hover:bg-accent border-border text-foreground'
                      }`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                      <span className="opacity-70 font-mono">({l.code})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <SimpleTooltip content="ISO 639-1 language code (e.g. en, es, fr)">
                    <Label htmlFor="code">
                      {t('languages:languages.code', 'ISO Code')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    id="code"
                    placeholder="fr, pt, de..."
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={!!editingItem?.isSystem}
                    required
                  />
                </div>

                <div>
                  <SimpleTooltip content="Display name of the language">
                    <Label htmlFor="name">
                      {t('languages:languages.name', 'Language Name')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    id="name"
                    placeholder="Français, Português..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <SimpleTooltip content="Emoji flag representing the language">
                    <Label htmlFor="flag">
                      {t('languages:languages.flag', 'Flag Emoji')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    id="flag"
                    placeholder="🇫🇷, 🇵🇹..."
                    value={flag}
                    onChange={(e) => setFlag(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-card">
                  <SimpleTooltip content={t('languages:languages.isDefaultDesc', 'Establece este idioma como principal del sistema')}>
                    <div className="font-semibold text-sm">
                      {t('languages:languages.isDefault', 'Default Language')}
                    </div>
                  </SimpleTooltip>
                  <Toggle
                    value={isDefault}
                    onChange={(v) => setIsDefault(v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-card">
                  <SimpleTooltip content={t('languages:languages.statusActiveDesc', 'Habilita la selección de este idioma para usuarios')}>
                    <div className="font-semibold text-sm">
                      {t('languages:languages.statusActive', 'Status (Active)')}
                    </div>
                  </SimpleTooltip>
                  <Toggle
                    value={enabled}
                    onChange={(v) => setEnabled(v)}
                    disabled={isDefault}
                  />
                </div>
              </div>
            </form>
          </div>
        </FormBox>

        {/* Card 2: AI Auto-Translation Generator (Built directly into Form) */}
        <FormBox
          title={t('languages:ai.title', 'AI Language Package Generator')}
          icon={<Sparkles className="h-4 w-4 text-amber-500" />}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SimpleTooltip content={t('languages:ai.subtitle', 'Generate complete language packs using configured AI Profiles')}>
                  <Label htmlFor="aiProfile">
                    {t('languages:ai.selectProfile', 'Select AI Profile:')}
                  </Label>
                </SimpleTooltip>
                <div className="flex items-center gap-2 mt-1">
                  <Cpu className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <select
                    id="aiProfile"
                    value={selectedAiProfile}
                    onChange={(e) => setSelectedAiProfile(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm font-medium"
                  >
                    {aiProfiles.length === 0 ? (
                      <option value="">{t('languages:ai.defaultAgent', 'Default AI (General Agent)')}</option>
                    ) : (
                      aiProfiles.map((p) => {
                        const displayName = p.profile_name || (p as any).name || `AI Profile #${p.id}`;
                        const displayModel = p.model || p.provider_name || '';
                        return (
                          <option key={p.id} value={p.id}>
                            {displayName} {displayModel ? `(${displayModel})` : ''}
                          </option>
                        );
                      })
                    )}
                  </select>
                </div>
              </div>

              <div className="flex items-end">
                <SimpleTooltip content={t('languages:ai.promptInfo', 'AI will preserve English string lengths to prevent menu and layout text overflow.')}>
                  <button
                    type="button"
                    onClick={handleRunAiTranslationFromForm}
                    disabled={isAiTranslating || !code || !name}
                    className="w-full px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm shadow-sm"
                  >
                    {isAiTranslating ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        {t('languages:ai.generating', 'AI Translating Interface...')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        {t('languages:ai.generateButton', 'Generate Full Translation with AI')}
                      </>
                    )}
                  </button>
                </SimpleTooltip>
              </div>
            </div>
          </div>
        </FormBox>

        {/* AI Translation Progress Modal (Localized) */}
        {showProgressModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 relative">
              <button
                type="button"
                onClick={() => {
                  setShowProgressModal(false);
                  setIsAiTranslating(false);
                  setShowForm(false);
                  setEditingItem(null);
                }}
                className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-3 pr-6">
                <div className="p-2 bg-amber-500/10 rounded-lg text-amber-500">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-foreground">
                    {translationComplete ? `🎉 ${t('languages:ai.modalCompleted', 'Translation Completed')}` : `${t('languages:ai.modalTitle', 'Translating to')} ${name} ${flag}`}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {translationComplete
                      ? t('languages:ai.modalSubCompleted', 'The language pack was generated and saved to database.')
                      : t('languages:ai.modalSubProgress', 'Processing i18n keys with AI Profile...')}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-medium">
                  <span className="text-muted-foreground">
                    {translationComplete
                      ? `${t('languages:ai.modalFinalized', 'Finalized')} (${translatedCount} ${t('languages:ai.modalKeys', 'keys')})`
                      : `${t('languages:ai.modalModule', 'Module')} (${translationStep.current}/${translationStep.total}): ${translationStep.namespace}`}
                  </span>
                  <span className="text-primary font-mono font-bold">
                    {translationComplete ? '100%' : `${translationStep.percent}%`}
                  </span>
                </div>
                <div className="w-full bg-muted h-3 rounded-full overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300 rounded-full"
                    style={{ width: translationComplete ? '100%' : `${translationStep.percent}%` }}
                  />
                </div>
              </div>

              {/* Status Indicator */}
              <div className="p-3 bg-muted/40 rounded-lg flex items-center gap-3 text-xs">
                {translationComplete ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">{t('languages:ai.modalSuccessLabel', 'Translation successful! Strings are live in memory.')}</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">{t('languages:ai.modalTranslatingHint', 'Analyzing JSON structure and translating with AI API...')}</span>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowProgressModal(false);
                    setIsAiTranslating(false);
                    setShowForm(false);
                    setEditingItem(null);
                  }}
                  className="px-5 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors text-sm shadow-sm"
                >
                  {t('languages:ai.closeModal', 'Close and View List')}
                </button>
              </div>
            </div>
          </div>
        )}

        </div>

        <FixedActionBar
          onSave={handleFormSubmit}
          onCancel={handleFormClose}
          saveText={t('languages:actions.save', 'Save')}
          cancelText={t('languages:actions.cancel', 'Cancel')}
          isLoading={isSaving}
        />
      </div>
    );
  }

  // RENDER 2: MAIN LIST VIEW & SUB-TABS (Matching Extensions / Voice Guides Standard)
  return (
    <div>
      {/* Sub-Tabs Switcher */}
      <div className="px-6 pt-4 flex border-b border-border gap-2 bg-background">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'list'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Globe className="h-4 w-4" />
          {t('languages:tabs.languages', 'Registered Languages')}
        </button>

        <button
          onClick={() => setActiveTab('editor')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'editor'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Edit3 className="h-4 w-4" />
          {t('languages:tabs.editor', 'String Key Editor')}
        </button>
      </div>

      {/* TAB 1: Registered Languages List (DataGridSimple) */}
      {activeTab === 'list' && (
        <>
          <DataGridSimple
            data={filteredLanguages}
            loading={loading}
            columns={visibleColumns}
            onColumnToggle={handleColumnToggle}
            pagination={pagination}
            onPageChange={setPage}
            onItemsPerPageChange={setLimit}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            renderActions={renderActions}
            searchPlaceholder={t('languages:searchPlaceholder', 'Search language...')}
            title={t('languages:title', 'Language Management')}
            titleIcon={<Globe className="h-5 w-5 text-primary" />}
            actions={
              <div className="flex items-center gap-2">
                <SimpleTooltip content={t('languages:languages.register', 'Create Language')} position="top">
                  <button
                    onClick={handleCreate}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium shadow-sm"
                  >
                    <Plus className="h-4 w-4" /> {t('languages:actions.add', 'Add')}
                  </button>
                </SimpleTooltip>
              </div>
            }
          />

          {/* Delete Custom Language Modal */}
          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, item: null })}
            onConfirm={handleConfirmDelete}
            title={t('languages:languages.deleteTitle', 'Eliminar Idioma')}
            message={deleteModal.item ? `¿Estás seguro de que deseas eliminar el idioma '${deleteModal.item.name}' (${deleteModal.item.code})? Esta acción eliminará permanentemente todas sus traducciones asociadas.` : ''}
            confirmText={t('languages:actions.delete', 'Eliminar')}
            cancelText={t('languages:actions.cancel', 'Cancelar')}
            type="danger"
          />
        </>
      )}

      {/* TAB 2: String Key-Value Editor */}
      {activeTab === 'editor' && (
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 bg-card p-4 rounded-xl border border-border shadow-sm">
            <div className="flex items-center gap-3">
              <Label className="text-sm font-medium">{t('languages:editor.selectLanguage', 'Language to Edit:')}</Label>
              <select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                className="px-3 py-1.5 border border-border rounded-lg bg-background text-sm font-medium"
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.flag} {l.name} ({l.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                <Input
                  placeholder={t('languages:editor.searchPlaceholder', 'Search key or translation text...')}
                  className="pl-9 w-72 text-sm"
                  value={editorSearch}
                  onChange={(e) => setEditorSearch(e.target.value)}
                />
              </div>

              <select
                value={selectedNs}
                onChange={(e) => setSelectedNs(e.target.value)}
                className="px-3 py-1.5 border border-border rounded-lg bg-background text-sm font-medium"
              >
                <option value="all">{t('languages:editor.allModules', 'All Modules')} ({availableNamespaces.length})</option>
                {availableNamespaces.map((ns) => (
                  <option key={ns} value={ns}>{ns}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase border-b border-border">
                <tr>
                  <th className="px-4 py-3 font-semibold">{t('languages:editor.moduleHeader', 'Module')}</th>
                  <th className="px-4 py-3 font-semibold">{t('languages:editor.keyHeader', 'Key')}</th>
                  <th className="px-4 py-3 font-semibold">{t('languages:editor.overrideHeader', 'Custom Translation (Override)')}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t('languages:editor.actionHeader', 'Action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredEditorStrings.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      {t('languages:editor.noResults', 'No translation strings found matching search query.')}
                    </td>
                  </tr>
                ) : (
                  filteredEditorStrings.map(({ ns, key, val, isOverride }) => {
                    const isEditing = editingKey?.ns === ns && editingKey?.key === key;
                    return (
                      <tr key={`${ns}:${key}`} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-primary">{ns}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-foreground">{key}</td>
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <Input
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="text-sm py-1"
                            />
                          ) : (
                            <span className={`text-foreground ${isOverride ? 'font-medium text-amber-500' : ''}`}>
                              {val}
                              {isOverride && (
                                <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-amber-500/10 text-amber-500 rounded font-mono">
                                  override
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <button
                              onClick={() => handleSaveTranslation(ns, key, editingValue)}
                              className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90"
                            >
                              {t('languages:editor.save', 'Save')}
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingKey({ ns, key });
                                setEditingValue(val);
                              }}
                              className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-accent"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default LanguageManagementPage;
